import React from "react";
import { ExtendedWarrantyCard } from "../extended-warranty/ExtendedWarrantyScreenWrapper";
import { CheckCircle } from "lucide-react";

const RefinancingSurveyReadOnlySummary = ({
  sortedQuestions,
  answers,
  previousResponses,
  onContinue,
}) => {
  if (!sortedQuestions.length || !previousResponses) return null;

  const formatCurrency = (value) => {
    if (!value) return "";
    // remove all non-digits before formatting (in case raw value was "10000")
    const num = parseFloat(String(value).replace(/[^0-9.]/g, ""));
    if (isNaN(num)) return value; // fallback

    return num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const getDisplayValue = (question, response) => {
    if (question.type === "CHOICE") {
      return (
        response.selections[0]?.optionText ||
        response.selections[0]?.optionValue ||
        ""
      );
    } else if (question.type === "TEXT") {
      switch (question.questionType) {
        case "MONEY":
          // Add $ sign for money type
          return `$` + formatCurrency(response.answer);
        case "DATE":
          // Format date if needed
          return response.answer
            ? new Date(response.answer).toLocaleDateString()
            : response.answer;
        case "NUMERIC":
          // Ensure numeric values are displayed correctly
          return response.answer || "";
        default:
          // Default text type
          return response.answer || "";
      }
    }
    return "";
  };

  return (
    <ExtendedWarrantyCard>
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">
            Refinancing Survey Summary
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            Review your previous answers below
          </p>
        </div>

        {/* Answers Summary */}
        <div className="space-y-4">
          {sortedQuestions.map((question, index) => {
            const response = previousResponses.responses.find(
              (resp) => resp.questionId === question.id
            );

            if (!response) return null;

            const displayValue = getDisplayValue(question, response);

            return (
              <div
                key={question.id}
                className="border-b border-gray-100 pb-4 last:border-b-0"
              >
                <div className="flex items-start gap-3">
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex-shrink-0 mt-1">
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 text-sm mb-1">
                      {question.prompt}
                    </h4>
                    <p className="text-gray-700 text-sm">{displayValue}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Continue Button */}
        <button
          onClick={onContinue}
          className="w-full flex uppercase font-bold items-center justify-center gap-2 bg-purpleGradient text-white px-4 py-3 rounded-lg hover:bg-purpleGradientDark transition-colors"
        >
          Continue
        </button>
      </div>
    </ExtendedWarrantyCard>
  );
};

export default RefinancingSurveyReadOnlySummary;
